import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { getDirectoryPlumbers, getDirectoryPostcodes, type DirectoryExtraFilters } from '../../lib/api'
import { DIRECTORY_PLUMBERS_PAGE_SIZE, DIRECTORY_MAP_PLUMBER_LIMIT } from '../../lib/directoryConstants'
import { useDirectoryPlumbersInfinite } from '../../hooks/useDirectoryPlumbersInfinite'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { directoryCanonicalPath } from '../../lib/directoryCanonical'
import { DirectorySidebar } from '../../components/directory/DirectorySidebar'
import { DirectoryPlumberList } from '../../components/directory/DirectoryPlumberList'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL, slugToLabel } from './directoryUtils'
import { DirectorySearchBar } from '../../components/directory/DirectorySearchBar'
import { buildPlumberHref, toPlumberPins } from '../../lib/plumberPins'

export function DirectoryPostcode() {
  const { countrySlug, countySlug, townSlug, postcodeSlug } = useParams<{
    countrySlug: string
    countySlug: string
    townSlug: string
    postcodeSlug: string
  }>()

  const { isEmergency, isGasSafe, isWaterSafe, basePath, label } = useDirectoryMode()
  const [extraFilters, setExtraFilters] = useState<DirectoryExtraFilters>({})

  const fetchPlumbers = useCallback(
    (skip: number) =>
      getDirectoryPlumbers(countrySlug!, countySlug!, townSlug!, postcodeSlug!, isEmergency, isGasSafe, isWaterSafe, {
        skip,
        limit: DIRECTORY_PLUMBERS_PAGE_SIZE,
      }, extraFilters),
    [countrySlug, countySlug, townSlug, postcodeSlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
  )

  const {
    data: plumberPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useDirectoryPlumbersInfinite(
    ['directory', 'plumbers', countrySlug, countySlug, townSlug, postcodeSlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
    fetchPlumbers,
    !!(countrySlug && countySlug && townSlug && postcodeSlug),
  )

  const plumbers = plumberPages?.pages.flatMap(p => p.plumbers) ?? []
  const listTotal = plumberPages?.pages[0]?.total
  const emergencyTotal = plumberPages?.pages[0]?.emergencyTotal ?? 0

  const countryLabel  = slugToLabel(countrySlug)
  const countyLabel   = slugToLabel(countySlug)
  const townLabel     = slugToLabel(townSlug)
  const postcodeLabel = postcodeSlug?.toUpperCase() ?? ''

  const hrefCtx = { basePath, countrySlug: countrySlug!, countySlug: countySlug!, townSlug: townSlug! }

  // Sibling postcodes in the same town — nearby-area cross-links.
  const { data: siblingPostcodes } = useQuery({
    queryKey: ['directory', 'sibling-postcodes', countrySlug, countySlug, townSlug],
    queryFn: () => getDirectoryPostcodes(countrySlug!, countySlug!, townSlug!),
    enabled: !!(countrySlug && countySlug && townSlug),
    staleTime: 5 * 60 * 1000,
  })
  const nearbyPostcodeLinks = (siblingPostcodes ?? [])
    .filter(p => p.postcodeSlug !== postcodeSlug)
    .slice(0, 12)
    .map(p => ({ label: p.postcodeArea, href: `${basePath}/${countrySlug}/${countySlug}/${townSlug}/${p.postcodeSlug}` }))

  const { data: mapData } = useQuery({
    queryKey: ['directory', 'plumbers-map', 'postcode', countrySlug, countySlug, townSlug, postcodeSlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryPlumbers(countrySlug!, countySlug!, townSlug!, postcodeSlug!, isEmergency, isGasSafe, isWaterSafe, { skip: 0, limit: DIRECTORY_MAP_PLUMBER_LIMIT }, {}),
    enabled: !!(countrySlug && countySlug && townSlug && postcodeSlug),
    staleTime: 5 * 60 * 1000,
  })
  const plumberPins = useMemo(
    () => toPlumberPins(mapData?.plumbers ?? [], p => buildPlumberHref(p, hrefCtx)),
    [mapData, basePath, countrySlug, countySlug, townSlug],
  )

  const count = listTotal ?? 0

  const faqs = [
    {
      q: `How much does a plumber cost in ${postcodeLabel}?`,
      a: `Emergency call-out fees in ${postcodeLabel} typically range from £60–£150 depending on the time of day. Browse individual profiles to see published rates.`,
    },
    {
      q: `Can I get a plumber in ${postcodeLabel} today?`,
      a: `${emergencyTotal > 0 && count > 0 ? `Yes - ${emergencyTotal} of the ${count} plumbers listed here offer 24/7 emergency cover. ` : ''}Use FixDelivered's emergency service for same-day dispatch.`,
    },
    {
      q: 'Do I need a Gas Safe plumber?',
      a: 'For any gas work - boiler repair, central heating, gas safety inspections - your plumber must be Gas Safe Registered by law. Gas Safe numbers are shown on profiles where available.',
    },
  ]

  return (
    <PageWrapper>
      <Seo
        title={`${count > 0 ? `${count} ` : ''}${label} in ${postcodeLabel}, ${townLabel} | FixDelivered`}
        description={`${count > 0 ? `${count} verified` : 'Find'} ${label.toLowerCase()} in ${postcodeLabel}, ${townLabel}${emergencyTotal > 0 ? `, ${emergencyTotal} available 24/7` : ''}. Compare ratings & call-out fees, or let us confirm one — usually within the hour.`}
        path={directoryCanonicalPath(countrySlug, countySlug, townSlug, postcodeSlug)}
        noindex={listTotal === 0}
        breadcrumbs={[
          { name: 'UK', url: basePath },
          { name: countryLabel, url: `${basePath}/${countrySlug}` },
          { name: countyLabel, url: `${basePath}/${countrySlug}/${countySlug}` },
          { name: townLabel, url: `${basePath}/${countrySlug}/${countySlug}/${townSlug}` },
          { name: postcodeLabel, url: `${basePath}/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}` },
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
          ...(plumbers.length ? [{
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `Plumbers in ${postcodeLabel}`,
            itemListElement: plumbers.slice(0, 25).map((p, i) => ({
              '@type': 'ListItem', position: i + 1,
              url: `https://fixdelivered.com${buildPlumberHref(p, hrefCtx)}`,
              name: p.companyName,
            })),
          }] : []),
        ]}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <Breadcrumb items={[
              { label: countryLabel, href: `${basePath}/${countrySlug}` },
              { label: countyLabel, href: `${basePath}/${countrySlug}/${countySlug}` },
              { label: townLabel, href: `${basePath}/${countrySlug}/${countySlug}/${townSlug}` },
              { label: postcodeLabel },
            ]} />

            <div className="mt-6 mb-4">
              <h1 className="text-2xl font-bold text-gray-900">{label} in {postcodeLabel}</h1>
              <p className="text-gray-500 mt-1 text-sm min-h-[2.75rem] sm:min-h-[1.5rem]">
                {listTotal && listTotal > 0
                  ? `${listTotal} verified plumbers${emergencyTotal > 0 ? ` · ${emergencyTotal} available 24/7` : ''}. `
                  : ''}
                {townLabel}, {countyLabel}
              </p>
              <DirectorySearchBar className="mt-3" />
            </div>

            <DirectoryPlumberList
              plumbers={plumbers}
              isLoading={isLoading}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              listTotal={listTotal}
              extraFilters={extraFilters}
              onFiltersChange={setExtraFilters}
              hrefBuilder={p => buildPlumberHref(p, hrefCtx)}
              heading={`Plumbers in ${postcodeLabel}`}
              emptyAreaLabel="this postcode area"
              backLink={{ to: `${basePath}/${countrySlug}/${countySlug}/${townSlug}`, label: `Back to ${townLabel}` }}
            />
          </div>

          <div className={DIRECTORY_SIDEBAR_COL}>
            <DirectorySidebar
              count={isLoading ? undefined : listTotal}
              areaLabel={postcodeLabel}
              emergencyCount={emergencyTotal}
              plumberPins={plumberPins}
              nearby={{ title: `Nearby postcodes in ${townLabel}`, links: nearbyPostcodeLinks }}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}
